/**
 * @module useReportCardUtils
 * @description Utility composable for extracting and formatting report card display data.
 * Transforms raw student result and attendance data into presentable formats.
 *
 * @example
 * ```typescript
 * const {
 *   studentName,
 *   studentClass,
 *   leftColumnInfo,
 *   rightColumnInfo,
 *   feedbacks
 * } = useReportCardUtils(studentResult, attendanceResult, currentSessionData, calendars, selectedTerm);
 * ```
 *
 * @param studentResult - Ref containing student result from GraphQL
 * @param attendanceResult - Ref containing attendance data
 * @param currentSessionData - Ref containing session remarks data
 * @param schoolCalendars - Ref array of academic calendars
 * @param selectedTerm - Ref of currently selected term ID
 *
 * @returns
 * - `studentName`: Formatted full name of the student
 * - `studentClass`: Classroom name
 * - `leftColumnInfo`: Object with admission info, session, term, gender
 * - `rightColumnInfo`: Object with attendance summary (opened, present, absent)
 * - `teacherComment`, `principalComment`: Remark strings
 * - `nextTermDate`: Formatted next term resumption date
 * - `feedbacks`: Array of feedback definitions with saved values
 *
 * @used_by
 * - ViewResults.vue (via useViewResults composable)
 */
import type { AcademicCalendar, ProcessedResult, ResultTemplateFeedback, StudentResultFeedback } from '@/types/composition-functions';
import { formatRating, isRatingItem } from '@/utils/resultFeedbackRating';
import { computed, ref, watch, type Ref } from 'vue';

type FeedbackRow = ResultTemplateFeedback & {
  value: string;
  rating: number | null;
  feedback_id?: string;
  updated_at?: unknown;
};

export function useReportCardUtils(
  studentResult: Ref<ProcessedResult | null | undefined>,
  attendanceResult: Ref<Record<string, unknown> | null | undefined>,
  currentSessionData: Ref<Record<string, unknown> | null | undefined>,
  schoolCalendars: Ref<AcademicCalendar[]>,
  selectedTerm: Ref<string | undefined>,
) {

  const studentName = computed(() => {
    if (studentResult.value?.student) {
      const s = studentResult.value.student;
      return `${s.surname} ${s.first_name} ${s.other_name || ''}`.trim();
    }
    return '';
  });

  const studentClass = computed(() => studentResult.value?.classroom?.name || '');

  const leftColumnInfo = computed(() => {
    const s = studentResult.value?.student;
    const calendar = studentResult.value?.academic_calendar;

    return {
      'Admission Number': s?.matric_no || '-',
      'Academic Session': calendar?.session?.name || '-',
      'Term': calendar?.termStructure?.name || '-',
      'Gender': s?.gender || '-',
    };
  });

  const rightColumnInfo = computed(() => {
    const summary = attendanceResult.value?.studentAttendance?.summary;
    return {
      'No of times school opened': summary?.total ?? '-',
      'No of times present': summary?.present ?? '-',
      'No of times absent': summary?.absent ?? '-',
    };
  });

  const teacherComment = computed(() => currentSessionData.value?.teacherRemark || 'No remark yet.');
  const principalComment = computed(() => currentSessionData.value?.principalRemark || 'No remark yet.');

  const nextTermDate = computed(() => {
    const calendar = schoolCalendars.value?.find((c) => c.id === selectedTerm.value);
    if (calendar?.next_calender_starts_at) {
      return new Date(calendar.next_calender_starts_at).toLocaleDateString('en-GB');
    }
    return '-';
  });

  const savedFeedbacks = computed(() => {
    const definitions = studentResult.value?.template?.feedbacks || [];
    const values = studentResult.value?.result_feedbacks || [];

    return definitions.map((def: ResultTemplateFeedback) => {
      const val = values.find((v: StudentResultFeedback) => v.result_template_feedback?.id === def.id);
      // A rating item (#574 phase 4) shows "Very Good (4/5)" wherever the
      // plain text value is printed; teachers edit `rating` instead.
      const rating = val?.rating ?? null;
      return {
        ...def,
        value: isRatingItem(def) ? formatRating(val?.rating_label, rating, def.rating_max) : (val?.comment || ''),
        rating,
        feedback_id: val?.id,
        updated_at: val?.updated_at,
      };
    }).sort((a, b) => a.sequence - b.sequence);
  });

  /**
   * The rows the report card edits. A computed hands out plain objects, so a
   * rating picked through v-model never re-rendered its "Very Good (4/5)" text
   * and a later recompute silently discarded the pick. These rows live in a
   * ref (deeply reactive) and are re-seeded from the saved data, keeping any
   * unsaved rating the user picked since the last load (#574).
   */
  const feedbacks = ref<FeedbackRow[]>([]);
  let lastSavedRatings = new Map<string, unknown>();

  watch(savedFeedbacks, (rows) => {
    const editedRatings = new Map<string, unknown>();
    for (const row of feedbacks.value) {
      const key = String(row.id);
      if (lastSavedRatings.has(key) && row.rating !== lastSavedRatings.get(key)) {
        editedRatings.set(key, row.rating);
      }
    }

    lastSavedRatings = new Map(rows.map((row) => [String(row.id), row.rating]));
    feedbacks.value = rows.map((row) => {
      const key = String(row.id);
      return editedRatings.has(key) && editedRatings.get(key) !== row.rating
        ? { ...row, rating: editedRatings.get(key) as number | null }
        : { ...row };
    });
  }, { immediate: true });

  const categorizedFeedbacks = computed(() => {
    const groups: Record<string, Record<string, unknown>[]> = {};
    const general: Record<string, unknown>[] = [];

    feedbacks.value.forEach((f: Record<string, unknown>) => {
      if (f.category && f.category.toLowerCase() !== 'general') {
        const cat = f.category; // Keep original casing for display label if needed, or normalize
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(f);
      } else {
        general.push(f);
      }
    });

    return { groups, general };
  });

  const styleCode = computed(() => studentResult.value?.template?.style_code || 'classic');
  const chartColumn = computed(() => studentResult.value?.template?.properties?.chart?.column || '');

  return {
    studentName,
    studentClass,
    leftColumnInfo,
    rightColumnInfo,
    teacherComment,
    principalComment,
    nextTermDate,
    feedbacks,
    categorizedFeedbacks,
    styleCode,
    chartColumn,
  };
}
